<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from "reka-ui";
import { DialogContent, DialogPortal, injectDialogRootContext, useForwardPropsEmits } from "reka-ui";
import SimpleDialogOverlay from "./SimpleDialogOverlay.vue";

const props = defineProps<DialogContentProps & {
  variant?: "default" | "small" | "large" | "x-large" | "xx-large";
  backgroundColor?: "grey" | "white";
  customWidth?: string;
}>();
const emits = defineEmits<DialogContentEmits>();
const forwarded = useForwardPropsEmits(props, emits);

const { onOpenChange } = injectDialogRootContext();

function handleClick() {
  onOpenChange(false);
}
</script>

<script lang="ts">
// from reka-ui docs to prevent closing of modal when clicking on scrollbar.
function preventCloseOnScrollbarClick(event: CustomEvent) {
  const originalEvent = event.detail.originalEvent;
  const target = originalEvent.target as HTMLElement;
  if (originalEvent.offsetX > target.clientWidth || originalEvent.offsetY > target.clientHeight) {
    event.preventDefault();
  }
}
</script>

<template>
  <DialogPortal>
    <SimpleDialogOverlay />
    <DialogContent
      v-bind="forwarded"
      class="fixed inset-0 w-screen overflow-y-auto pt-0 focus:outline-hidden data-[state=closed]:animate-dialog-out data-[state=open]:animate-dialog-in"
      @pointer-down-outside="preventCloseOnScrollbarClick"
    >
      <div
        class="grid min-h-full grid-rows-[minmax(0,15%)_auto_minmax(0,15%)] justify-items-center p-4"
        @click.self="handleClick"
      >
        <div
          class="row-start-2 mb-auto grid w-full min-w-0 gap-4 rounded-sm border-slate-200/50 p-6 shadow-[0_1px_20px_0,0_1px_20px_-1px] shadow-black/5"
          :class="{
            'sm:max-w-sm': variant === 'small',
            'sm:max-w-lg': !variant || variant === 'default',
            'sm:max-w-2xl': variant === 'large',
            'sm:max-w-3xl': variant === 'x-large',
            'sm:max-w-5xl': variant === 'xx-large',
            'bg-slate-100': backgroundColor === 'grey',
            'bg-white': !backgroundColor || backgroundColor === 'white',
          }"
        >
          <slot />
        </div>
      </div>
    </DialogContent>
  </DialogPortal>
</template>
